import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
// Subpath import (same object as antd's Layout) so the module stays loadable
// under bun test, which can't resolve `Layout` from antd's CJS root.
import Layout from 'antd/lib/layout';
import {
  ExceptionOutlined,
  FormOutlined,
} from '@ant-design/icons';
import AnnounceKit from 'announcekit-react';
import axios from 'axios';
import {
  ChartLine, ChatsCircle, PencilSimple, Phone, Robot, UserSwitch,
} from '@phosphor-icons/react';
import {
  AddressBookSvg, CloudArrowUpSvg, GearSvg, HouseSvg, KeySvg, MailboxSvg, MegaphoneSvg, PaperPlaneTiltSvg, QuestionSvg, SignOutSvg,
  UsersSvg,
} from '../../assets/img/redesign/svgPaths';
import {
  verifyFeatureAccess,
  loadFeatureSettings,
  verifyPatientEngagementAccess,
  verifyPatientRecaptureAccess,
  verifyReceivedInboxAccess,
  verifySentInboxAccess,
  verifyVoiceInboxAccess,
  USER_ACTIVITY_ANALYTICS_FEATURE,
} from '../../global/featureFlags';
import { captureAnalytics } from '../../actions/preload';
import {
  closeNavCustomizer as closeNavCustomizerAction,
  openNavCustomizer as openNavCustomizerAction,
  saveNavPreferences as saveNavPreferencesAction,
} from '../../actions/navCustomization';
import './newNav.scss';
import MenuItems from './MenuItems';
import CustomizeNavModal from './CustomizeNavModal';
import { applyNavPreferences } from './navPreferences';

const { Sider } = Layout;

// Fixed entries pinned below the user's pages; never reordered or hidden.
const UTILITY_NAV_KEYS = new Set(['updates', 'portal-settings', 'settings', 'guide', 'admin-login', 'sign-out']);

// React bubbles events from portals (the editor modal, the chip) through
// .new-nav, so only react to events from the nav's own DOM.
const fromNavDom = (event) => event.currentTarget.contains(event.target);

const NewNav = ({
  auth,
  guest,
  createGuest,
  user,
  currentOrganizationId,
  logout,
  toggle,
  openModal,
  faxSource,
  redirectToCreateGuestAccount,
  loginErrorMessage,
  adminLoginSuccess,
  loginAdmin,
  cable,
  setNumberOfNewReferrals,
  incrementReferralCount,
  decrementReferralCount,
  newReferralsCount,
  history,
  isAdmin,
  referralManagement,
  viewReferralAnalytics,
  navPreferences,
  navEditorOpen,
  navEditorSource,
  navEditorSession,
  openNavCustomizer,
  closeNavCustomizer,
  saveNavPreferences,
}) => {
  const [canViewVoiceAi, setCanViewVoiceAi] = useState(false);
  const [hasUserActivity, setHasUserActivity] = useState(false);
  const [canAccessReceivedInbox, setCanAccessReceivedInbox] = useState(true);
  const [canAccessSentInbox, setCanAccessSentInbox] = useState(true);
  const [canAccessVoiceInbox, setCanAccessVoiceInbox] = useState(true);
  const [hasNoFeatures, setHasNoFeatures] = useState(false);
  const [hasReferralPortal, setHasReferralPortal] = useState(false);
  const [canAccessPatientRecapture, setCanAccessPatientRecapture] = useState(false);
  const [canSeeRecaptureUpsell, setCanSeeRecaptureUpsell] = useState(false);
  const [canSeeArmUpsell, setCanSeeArmUpsell] = useState(false);
  const [canSeePatientEngagementUpsell, setCanSeePatientEngagementUpsell] = useState(false);
  // Initialized false so a rejected feature-access await can only hide the
  // Patient Engagement entry, never flash it.
  const [canAccessPatientEngagement, setCanAccessPatientEngagement] = useState(false);
  const [hasInboundShares, setHasInboundShares] = useState(false);
  const [hasCustomizableNav, setHasCustomizableNav] = useState(false);
  const [showCustomizeChip, setShowCustomizeChip] = useState(false);
  const hideCustomizeChipTimer = useRef(null);
  const [unreadNotifications, setUnreadNotifications] = useState(false);
  const announceKitRef = useRef(null);
  const canViewReferralAnalytics = (isAdmin && referralManagement) || viewReferralAnalytics;
  const canViewMairaAnalytics = isAdmin && canViewVoiceAi;
  const canViewUserActivity = isAdmin && hasUserActivity;

  useEffect(() => {
    const getFeatureAccess = async () => {
      setCanViewVoiceAi(await verifyFeatureAccess('voice_ai'));
      setHasUserActivity(await verifyFeatureAccess(USER_ACTIVITY_ANALYTICS_FEATURE));
      setCanAccessReceivedInbox(await verifyReceivedInboxAccess());
      setCanAccessSentInbox(await verifySentInboxAccess());
      setCanAccessVoiceInbox(await verifyVoiceInboxAccess());
      setHasNoFeatures(await verifyFeatureAccess('no_features'));
      setHasReferralPortal(await verifyFeatureAccess('referral_portal'));
      // ARM upsell shows for admins whose org has arm_upsell_flag but does NOT
      // yet have arm_enabled.
      const [armEnabled, armUpsellEnabled] = await Promise.all([
        verifyFeatureAccess('arm_enabled'),
        verifyFeatureAccess('arm_upsell_flag'),
      ]);
      setCanSeeArmUpsell(Boolean(armUpsellEnabled) && !armEnabled && isAdmin);
      // Patient Engagement access is role-based — backend-derived
      // m8_patient_engagement key (User#patient_engagement). Read before the
      // upsell flags so a failed flag read cannot hide the product entry.
      setCanAccessPatientEngagement(await verifyPatientEngagementAccess());
      setHasCustomizableNav(await verifyFeatureAccess('customizable_nav'));
      // Patient Engagement upsell shows for admins whose org has
      // patient_engagement_upsell_flag but does NOT yet have patient_engagement.
      const [peEnabled, peUpsellEnabled] = await Promise.all([
        verifyFeatureAccess('patient_engagement'),
        verifyFeatureAccess('patient_engagement_upsell_flag'),
      ]);
      setCanSeePatientEngagementUpsell(Boolean(peUpsellEnabled) && !peEnabled && isAdmin);
    };

    if (user) {
      getFeatureAccess();
    }
  }, [user, isAdmin]);

  useEffect(() => {
    let active = true;
    setCanAccessPatientRecapture(false);
    setCanSeeRecaptureUpsell(false);
    const checkRecapture = async () => {
      if (!user) return;
      const [hasRecapture, upsellEnabled, signupEnabled, signup] = await Promise.all([
        verifyPatientRecaptureAccess().catch(() => false),
        verifyFeatureAccess('recapture_upsell_flag').catch(() => false),
        verifyFeatureAccess('recapture_self_serve_signup').catch(() => false),
        loadFeatureSettings('recapture_signup').catch(() => ({})),
      ]);
      if (!active) return;
      // RecaptureEntry wraps both routes and shows a pending request on either,
      // so an org with Recapture access gets the ordinary workspace item.
      setCanAccessPatientRecapture(hasRecapture);
      setCanSeeRecaptureUpsell(Boolean(
        isAdmin && !hasRecapture && (signup.has_request || signupEnabled || upsellEnabled),
      ));
    };
    checkRecapture();
    return () => { active = false; };
  }, [user, isAdmin, currentOrganizationId]);

  // For non-portal-only orgs, check if they have inbound referral shares
  // to decide whether to show the portal nav icon.
  useEffect(() => {
    if (!hasReferralPortal || hasNoFeatures) return;

    let active = true;
    let requestId = 0;
    let latestSuccessfulRequestId = 0;
    const refreshPortalAccess = () => {
      requestId += 1;
      const currentRequestId = requestId;
      axios.get('/api/v1/portal/referrals/has_portal_access')
        .then(({ data }) => {
          if (!active || currentRequestId < latestSuccessfulRequestId) return;
          latestSuccessfulRequestId = currentRequestId;
          setHasInboundShares(data.has_portal_access);
        })
        .catch(() => {
          // Keep the last confirmed value through transient network failures.
        });
    };
    setHasInboundShares(false);
    refreshPortalAccess();
    window.addEventListener('focus', refreshPortalAccess);
    return () => {
      active = false;
      window.removeEventListener('focus', refreshPortalAccess);
    };
  }, [hasReferralPortal, hasNoFeatures, user]);

  useEffect(() => {
    if (auth && referralManagement) {
      const cableSubscription = cable.subscriptions.create(
        {
          channel: 'ReferralsChannel',
        },
        {
          received: (message) => {
            if (message === 'increment') {
              incrementReferralCount();
            } else if (message === 'decrement') {
              decrementReferralCount();
            } else {
              setNumberOfNewReferrals(message);
            }
          },
        },
      );
      return () => cableSubscription.unsubscribe();
    }
    return undefined;
  }, [auth, cable, referralManagement, incrementReferralCount, decrementReferralCount, setNumberOfNewReferrals]);

  useEffect(() => {
    if (loginErrorMessage) {
      /* global toastr */
      toastr.error(loginErrorMessage);
    }
  }, [loginErrorMessage]);

  useEffect(() => {
    if (adminLoginSuccess) {
      const win = window.open(`${window.location.origin}/admin`, '_blank');
      win.opener = null;
    }
  }, [adminLoginSuccess]);

  useEffect(() => {
    if (announceKitRef.current && !unreadNotifications) {
      announceKitRef.current.unread().then((unread) => {
        if (unread !== 0) {
          setUnreadNotifications(true);
        }
      });
    }
  }, [announceKitRef.current]);

  const isOnChromeExt = window.location.pathname.includes('/app/chrome/');

  const onMobile = window.innerWidth < 800;

  const openNewUpdates = () => {
    announceKitRef.current.open();
  };

  // UX Decision: When hasNoFeatures is true, we show all nav buttons as disabled rather than
  // hiding them. This lets users see what features exist in the full product, encouraging
  // potential upgrades while clearly indicating they don't have access.
  const shouldDisableNavButtons = hasNoFeatures;

  const withDisabled = (button, allowWhenNoFeatures = false) => (button ? {
    ...button,
    disabled: shouldDisableNavButtons && !allowWhenNoFeatures,
  } : button);

  // Portal-only orgs (no_features + referral_portal) always see the portal.
  // Regular orgs only see it if they have active inbound referral shares.
  const showPortal = hasReferralPortal && (hasNoFeatures || hasInboundShares);

  const referralPortalButton = showPortal && withDisabled({
    label: 'Referral Portal',
    link: '/app/referral_portal',
    icon: <FormOutlined style={{ paddingBottom: 2 }} />,
    key: 'referral_portal',
    onClick: () => history.push('/app/referral_portal'),
  }, true);
  const showReferralPortalAtTop = hasNoFeatures && showPortal;
  const showReferralPortalAfterReferrals = !hasNoFeatures && showPortal;

  const allNavButtons = [
    showReferralPortalAtTop && referralPortalButton,
    withDisabled({
      label: 'Home',
      link: '/app',
      icon: HouseSvg,
      key: 'home',
      onClick: () => history.push('/app'),
    }),
    withDisabled({
      label: 'Uploads',
      link: '/app/uploads',
      icon: CloudArrowUpSvg,
      key: 'uploads',
      onClick: () => history.push('/app/uploads'),
    }),
    withDisabled((hasNoFeatures || canAccessSentInbox) && {
      label: 'Sent',
      link: '/app/sent',
      icon: PaperPlaneTiltSvg,
      key: 'sent',
      onClick: () => history.push('/app/sent'),
    }),
    withDisabled((hasNoFeatures || canAccessReceivedInbox) && {
      label: 'Inbox',
      link: '/app/received',
      icon: MailboxSvg,
      key: 'inbox',
      onClick: () => history.push('/app/received'),
    }),
    withDisabled({
      label: 'Assigned',
      link: '/app/assigned',
      icon: <ExceptionOutlined />,
      key: 'assigned',
      onClick: () => history.push('/app/assigned'),
    }),
    withDisabled((hasNoFeatures || referralManagement) && {
      label: 'Referrals',
      link: '/app/referred',
      matchPaths: ['/app/referred/setup'],
      icon: UsersSvg,
      key: 'referrals',
      badgeCount: null,
      onClick: () => history.push('/app/referred'),
    }),
    showReferralPortalAfterReferrals && referralPortalButton,
    canSeeArmUpsell && !hasNoFeatures && withDisabled({
      label: 'ARM',
      link: '/app/arm',
      icon: <Robot size={24} weight="fill" />,
      key: 'arm-sales',
      onClick: () => history.push('/app/arm'),
    }),
    withDisabled((hasNoFeatures || canAccessVoiceInbox) && {
      label: 'Voice',
      link: '/app/voice_inbox',
      icon: <Phone size={24} weight="fill" />,
      key: 'voice_inbox',
      onClick: () => history.push('/app/voice_inbox'),
    }),
    withDisabled((hasNoFeatures || canViewReferralAnalytics || canViewMairaAnalytics || canViewUserActivity) && {
      label: 'Analytics',
      link: '/app/analytics',
      icon: <ChartLine size={24} weight="fill" />,
      key: 'analytics',
      onClick: () => history.push('/app/analytics'),
    }),
    canAccessPatientRecapture && withDisabled({
      label: 'Recapture',
      link: '/app/patient-recapture',
      icon: <UserSwitch size={24} weight="fill" />,
      key: 'patient-recapture',
      onClick: () => {
        captureAnalytics('recapture_nav_clicked', { organization_id: currentOrganizationId, source: 'app' });
        history.push('/app/patient-recapture?source=app');
      },
    }),
    canSeeRecaptureUpsell && !hasNoFeatures && withDisabled({
      label: 'Recapture',
      link: '/app/recapture',
      icon: <UserSwitch size={24} weight="fill" />,
      key: 'recapture-sales',
      onClick: () => {
        captureAnalytics('recapture_signup_nav_clicked', { organization_id: currentOrganizationId, source: 'app' });
        history.push('/app/recapture?source=app');
      },
    }),
    canAccessPatientEngagement && withDisabled({
      label: 'Patient Engagement',
      link: '/app/patient-engagement',
      icon: <ChatsCircle size={24} weight="fill" />,
      key: 'patient-engagement',
      onClick: () => {
        captureAnalytics('patient_engagement_nav_clicked');
        history.push('/app/patient-engagement');
      },
    }),
    canSeePatientEngagementUpsell && !hasNoFeatures && withDisabled({
      label: 'Patient Engagement',
      link: '/app/engagement',
      icon: <ChatsCircle size={24} weight="fill" />,
      key: 'patient-engagement-sales',
      onClick: () => {
        captureAnalytics('patient_engagement_upsell_nav_clicked');
        history.push('/app/engagement');
      },
    }),
    !hasNoFeatures && withDisabled({
      label: 'Contacts',
      link: '/app/contacts',
      icon: AddressBookSvg,
      key: 'contacts',
      onClick: () => history.push('/app/contacts'),
    }),
    withDisabled({
      label: 'Updates',
      icon: MegaphoneSvg,
      key: 'updates',
      dot: unreadNotifications,
      onClick: () => {
        openNewUpdates();
        setUnreadNotifications(false);
      },
    }, true),
    // Portal-only partner orgs get the admin-only portal settings page instead
    // of the regular app settings (the page itself and its API are also gated
    // to admins, so non-admins never see the entry).
    hasNoFeatures && showPortal && isAdmin && withDisabled({
      label: 'Settings',
      link: '/app/referral_portal/settings',
      icon: GearSvg,
      key: 'portal-settings',
      onClick: () => history.push('/app/referral_portal/settings'),
    }, true),
    !hasNoFeatures && withDisabled({
      label: 'Settings',
      link: '/app/user',
      icon: GearSvg,
      key: 'settings',
      onClick: () => history.push('/app/user'),
    }),
    !hasNoFeatures && withDisabled({
      label: 'Guide',
      link: '/app/guide',
      icon: QuestionSvg,
      key: 'guide',
      style: { position: 'absolute', bottom: '170px', width: '100%' },
      onClick: () => history.push('/app/guide'),
    }),
    !hasNoFeatures && withDisabled({
      label: 'Admin',
      icon: KeySvg,
      noHighlightOnSelect: true,
      key: 'admin-login',
      style: { position: 'absolute', bottom: '110px', width: '100%' },
      onClick: loginAdmin,
    }),
    withDisabled({
      label: 'Logout',
      icon: SignOutSvg,
      noHighlightOnSelect: true,
      key: 'sign-out',
      onClick: logout,
    }, true),
  ].filter(Boolean);

  // Behind customizable_nav. Portal-only orgs keep the designed nav; their
  // entries are mostly disabled.
  const canCustomizeNav = hasCustomizableNav && !hasNoFeatures;
  const pageButtons = allNavButtons.filter((button) => !UTILITY_NAV_KEYS.has(button.key));
  const utilityButtons = allNavButtons.filter((button) => UTILITY_NAV_KEYS.has(button.key));
  const navButtons = canCustomizeNav
    ? [...applyNavPreferences(pageButtons, navPreferences), ...utilityButtons]
    : allNavButtons;

  // The chip is portaled outside the nav, so hover is tracked on both. The short
  // delay lets the pointer cross from the nav onto the chip without a flicker.
  const revealCustomizeChip = () => {
    clearTimeout(hideCustomizeChipTimer.current);
    setShowCustomizeChip(true);
  };
  const concealCustomizeChip = () => {
    clearTimeout(hideCustomizeChipTimer.current);
    hideCustomizeChipTimer.current = setTimeout(() => setShowCustomizeChip(false), 150);
  };
  useEffect(() => () => clearTimeout(hideCustomizeChipTimer.current), []);

  const handleNavContextMenu = (event) => {
    if (!canCustomizeNav || !fromNavDom(event)) return;
    event.preventDefault();
    openNavCustomizer('rail_context_menu');
  };

  // `summary` is the editor's change counts for the nav_customized event.
  const handleSaveNavPreferences = async (preferences, summary) => {
    const result = await saveNavPreferences(preferences);
    if (result?.ok) {
      captureAnalytics('nav_customized', { source: navEditorSource, ...summary });
      closeNavCustomizer();
    }
    return result;
  };

  const announceKitUser = auth && user?.uid ? {
    id: user.uid,
    email: user.uid,
    name: user?.fullName,
  } : undefined;

  return (
  // hide the new nav bar before logging in and completing the walkthrough or if the user is on mobile
    (auth && window.location.pathname !== '/app/walkthrough' && !onMobile && !isOnChromeExt)
      && (
    // z-index set so nav will always be on top of the inbox sidebars that have a z-index of 1000
        <div
          className="new-nav"
          style={{ height: '100%', position: 'absolute', zIndex: 900 }}
          onContextMenu={handleNavContextMenu}
          onMouseEnter={canCustomizeNav ? (event) => { if (fromNavDom(event)) revealCustomizeChip(); } : undefined}
          onMouseLeave={canCustomizeNav ? concealCustomizeChip : undefined}
        >
          <Sider id="sidebar-nav" collapsible collapsed={true} trigger={null}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '100%',
            }}
            >
              <MenuItems buttons={navButtons} />
            </div>
            <AnnounceKit ref={announceKitRef} widget="https://releasenotes.medsender.com/widgets/v2/3FvLNK" user={announceKitUser} />
          </Sider>
          {canCustomizeNav && !navEditorOpen && createPortal(
            // Portaled to body: the nav's own stacking context (z-index 900)
            // sits under page sidebars (1000), which would cover the chip where
            // it overhangs the nav. Shown on nav hover or keyboard focus, and
            // not while the editor is open (it would sit above the mask).
            <button
              type="button"
              className={`new-nav-customize ${showCustomizeChip ? 'new-nav-customize--visible' : ''}`}
              aria-label="Customize navigation"
              title="Customize navigation"
              onMouseEnter={revealCustomizeChip}
              onMouseLeave={concealCustomizeChip}
              onClick={() => openNavCustomizer('rail')}
            >
              <PencilSimple size={14} weight="bold" />
            </button>,
            document.body,
          )}
          {canCustomizeNav && (
            <CustomizeNavModal
              // A new session per opening remounts the editor with fresh drafts.
              key={navEditorSession}
              visible={navEditorOpen}
              pages={pageButtons}
              preferences={navPreferences}
              onCancel={closeNavCustomizer}
              onSave={handleSaveNavPreferences}
            />
          )}
        </div>
      )
  );
};

NewNav.defaultProps = {
  user: '',
  currentOrganizationId: null,
  faxSource: '',
  navPreferences: null,
  navEditorOpen: false,
  navEditorSource: null,
  navEditorSession: 0,
  openNavCustomizer: () => {},
  closeNavCustomizer: () => {},
  saveNavPreferences: async () => ({ ok: false }),
};

NewNav.propTypes = {
  auth: PropTypes.bool.isRequired,
  guest: PropTypes.bool.isRequired,
  createGuest: PropTypes.bool.isRequired,
  user: PropTypes.object,
  currentOrganizationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  logout: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  faxSource: PropTypes.string,
  redirectToCreateGuestAccount: PropTypes.func.isRequired,
  adminLoginSuccess: PropTypes.bool.isRequired,
  loginErrorMessage: PropTypes.string.isRequired,
  loginAdmin: PropTypes.func.isRequired,
  cable: PropTypes.any.isRequired,
  setNumberOfNewReferrals: PropTypes.func.isRequired,
  incrementReferralCount: PropTypes.func.isRequired,
  decrementReferralCount: PropTypes.func.isRequired,
  newReferralsCount: PropTypes.number.isRequired,
  history: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  referralManagement: PropTypes.bool.isRequired,
  viewReferralAnalytics: PropTypes.bool.isRequired,
  navPreferences: PropTypes.shape({
    order: PropTypes.arrayOf(PropTypes.string),
    hidden: PropTypes.arrayOf(PropTypes.string),
  }),
  navEditorOpen: PropTypes.bool,
  navEditorSource: PropTypes.string,
  navEditorSession: PropTypes.number,
  openNavCustomizer: PropTypes.func,
  closeNavCustomizer: PropTypes.func,
  saveNavPreferences: PropTypes.func,
};

export { NewNav };

// withRouter supplies `location`, so the pure connect re-renders NewNav on each
// route change (MenuItems reads window.location to highlight the current page)
// without re-rendering on unrelated Redux updates.
export default withRouter(connect((state) => ({
  currentOrganizationId: state.auth.currentOrganizationId,
  navPreferences: state.navCustomization.preferences,
  navEditorOpen: state.navCustomization.editorOpen,
  navEditorSource: state.navCustomization.editorSource,
  navEditorSession: state.navCustomization.editorSession,
}), {
  openNavCustomizer: openNavCustomizerAction,
  closeNavCustomizer: closeNavCustomizerAction,
  saveNavPreferences: saveNavPreferencesAction,
})(NewNav));
